import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import {
  supabase,
  isSupabaseConfigured,
  transformContactToCamelCase,
  transformDealToCamelCase,
} from '../../../utils/supabase';
import { useVisibilityScope } from '../../../hooks/useVisibilityScope';
import { applyVisibilityScope, visibilityScopeKey } from '../../../utils/recordVisibility';
import { CLOSED_STAGES_LEGACY } from '../../../constants/crmConstants';

/**
 * Person-first omni search for the Workspace preview.
 *
 * One term finds PEOPLE, never dead-ends on entity type:
 *  - Contact match: word-AND ILIKE across name/email/phone/company on
 *    view_contact_summary (same proven pattern as useContactsTable's
 *    applyGlobalSearch), with a digits-only phone variant per word.
 *  - Deal-field match: parallel query on crm_deals (deal_name, loan_number,
 *    arive_id) mapped back to contact_id — pasting a loan number still finds
 *    the person who holds it.
 *
 * Every result is a person card: { contact, deals } where `deals` are the
 * person's borrower deals (open first) for the stage chips.
 */

const RECENTS_KEY = 'workspacePreviewRecentContacts';
const RECENTS_MAX = 12;
const SEARCH_DEBOUNCE_MS = 350;
export const MIN_SEARCH_CHARS = 2;
const CONTACT_RESULT_LIMIT = 25;
const DEAL_MATCH_LIMIT = 20;

// Minimal chip payload — enough for StageBadge + amount + identifiers.
const DEAL_CHIP_COLUMNS =
  'id, contact_id, deal_name, stage, amount, product, loan_number, arive_id, property_street, property_city, created_at, updated_at';

/* ------------------------------------------------------------------ */
/* Recently-opened people (localStorage)                                */
/* ------------------------------------------------------------------ */

export function getRecentContactIds() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (_) {
    return [];
  }
}

export function recordRecentContact(contactId) {
  if (!contactId) return;
  try {
    const next = [contactId, ...getRecentContactIds().filter((id) => id !== contactId)].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (_) {
    // localStorage unavailable — recents are a nicety, never block
  }
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

// PostgREST .or() treats commas/parens as syntax — strip them from user input
// (same defense as DealQuickSearchModal).
function sanitizeTerm(term) {
  return (term || '').replace(/[,()]/g, ' ').trim();
}

// One word's OR-group across the searchable contact columns. Words are AND'd
// by chaining .or() calls, so "David Young" matches first=David + last=Young.
function buildWordOrCondition(word) {
  const t = `%${word}%`;
  const parts = [
    `first_name.ilike.${t}`,
    `last_name.ilike.${t}`,
    `email.ilike.${t}`,
    `phone.ilike.${t}`,
    `company.ilike.${t}`,
  ];
  // "(904) 555-1234" style input: also try the digits-only form against the
  // phone column, which is stored in varying formats.
  const digits = word.replace(/\D/g, '');
  if (digits.length >= 3 && digits !== word) {
    parts.push(`phone.ilike.%${digits}%`);
  }
  return parts.join(',');
}

const isClosedStage = (stage) => CLOSED_STAGES_LEGACY.includes(stage);

/**
 * How loan officers identify a deal: "{lender loan #} — {street address}".
 * Falls back gracefully when either half is missing.
 */
export function dealDisplayLabel(deal) {
  if (!deal) return 'Deal';
  const street = (deal.propertyAddress?.street || '').trim();
  const loanNum = String(deal.loanNumber || '').trim();
  if (loanNum && street) return `${loanNum} — ${street}`;
  if (street) return street;
  if (loanNum) return `${loanNum} — ${deal.dealName || 'Deal'}`;
  if (deal.dealName) return deal.dealName;
  const product = (deal.product || 'Deal').toUpperCase();
  return deal.createdAt
    ? `${product} · started ${moment(deal.createdAt).format('MMM D, YYYY')}`
    : product;
}

/** Open deals first (newest first), then closed (newest first). */
export function sortDealsForDisplay(deals) {
  return [...(deals || [])].sort((a, b) => {
    const aClosed = isClosedStage(a.stage) ? 1 : 0;
    const bClosed = isClosedStage(b.stage) ? 1 : 0;
    if (aClosed !== bClosed) return aClosed - bClosed;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
}

export function countOpenDeals(deals) {
  return (deals || []).filter((d) => !isClosedStage(d.stage)).length;
}

/** Fetch borrower deals for a set of contacts, grouped by contact id. */
async function fetchDealChips(contactIds) {
  if (!contactIds.length) return {};
  const { data, error } = await supabase
    .from('crm_deals')
    .select(DEAL_CHIP_COLUMNS)
    .in('contact_id', contactIds);

  if (error || !data) return {};

  const byContact = {};
  for (const row of data) {
    const deal = transformDealToCamelCase(row);
    if (!byContact[row.contact_id]) byContact[row.contact_id] = [];
    byContact[row.contact_id].push(deal);
  }
  for (const id of Object.keys(byContact)) {
    byContact[id] = sortDealsForDisplay(byContact[id]);
  }
  return byContact;
}

async function fetchContactsByIds(ids) {
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from('view_contact_summary')
    .select('*')
    .in('id', ids);
  if (error || !data) return [];
  return data.map(transformContactToCamelCase);
}

/* ------------------------------------------------------------------ */
/* Hook                                                                 */
/* ------------------------------------------------------------------ */

/**
 * @param {string} term        Raw search input.
 * @param {'mine'|'all'} scope Owner scope ('mine' = the uid's effective book:
 *   owned + team book-shares + record shares; 'all' = admin All Users).
 * @param {string} uid         Target user's uid (for 'mine' scope).
 * @returns {{ people: Array<{contact: object, deals: Array}>, loading: boolean, isSearchMode: boolean }}
 */
export function useWorkspaceSearch(term, scope, uid) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  // Multi-owner visibility scopes. Contacts and deals resolve separately
  // because team book-shares carry independent share_contacts/share_deals flags.
  const targetUserId = scope === 'all' ? null : uid || null;
  const { scope: contactScope } = useVisibilityScope({ entityType: 'contact', targetUserId });
  const { scope: dealScope } = useVisibilityScope({ entityType: 'deal', targetUserId });
  const scopesKey = `${visibilityScopeKey(contactScope)}|${visibilityScopeKey(dealScope)}:g${(contactScope?.grantsVersion || 0) + (dealScope?.grantsVersion || 0)}`;

  const cleanTerm = sanitizeTerm(term);
  const isSearchMode = cleanTerm.length >= MIN_SEARCH_CHARS;

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setPeople([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    const run = async () => {
      try {
        let contacts = [];

        if (isSearchMode) {
          // --- Query A: contact-field match ---
          let contactQuery = supabase
            .from('view_contact_summary')
            .select('*')
            .order('updated_at', { ascending: false })
            .limit(CONTACT_RESULT_LIMIT);
          if (scope === 'mine') contactQuery = applyVisibilityScope(contactQuery, contactScope);
          const words = cleanTerm.split(/\s+/).filter((w) => w.length >= 1);
          for (const word of words) {
            contactQuery = contactQuery.or(buildWordOrCondition(word));
          }

          // --- Query B: deal-field match mapped back to the person ---
          const t = `%${cleanTerm}%`;
          let dealQuery = supabase
            .from('crm_deals')
            .select('contact_id')
            .or(`deal_name.ilike.${t},loan_number.ilike.${t},arive_id.ilike.${t}`)
            .not('contact_id', 'is', null)
            .limit(DEAL_MATCH_LIMIT);
          if (scope === 'mine') dealQuery = applyVisibilityScope(dealQuery, dealScope);

          const [contactRes, dealRes] = await Promise.all([contactQuery, dealQuery]);
          if (requestId !== requestIdRef.current) return;

          contacts = (contactRes.data || []).map(transformContactToCamelCase);

          const haveIds = new Set(contacts.map((c) => c.id));
          const dealContactIds = [
            ...new Set((dealRes.data || []).map((r) => r.contact_id).filter((id) => id && !haveIds.has(id))),
          ];
          if (dealContactIds.length) {
            const extra = await fetchContactsByIds(dealContactIds);
            if (requestId !== requestIdRef.current) return;
            // Deal-field matches go on top: if you pasted a loan number,
            // that person is almost certainly who you meant.
            contacts = [...extra, ...contacts];
          }
        } else {
          // --- Recents mode ---
          const recentIds = getRecentContactIds();
          if (recentIds.length) {
            const fetched = await fetchContactsByIds(recentIds);
            if (requestId !== requestIdRef.current) return;
            const byId = new Map(fetched.map((c) => [c.id, c]));
            contacts = recentIds.map((id) => byId.get(id)).filter(Boolean);
          }
        }

        // --- Hydrate deal chips in one query ---
        const chips = await fetchDealChips(contacts.map((c) => c.id));
        if (requestId !== requestIdRef.current) return;

        setPeople(contacts.map((contact) => ({ contact, deals: chips[contact.id] || [] })));
        setLoading(false);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error('[useWorkspaceSearch] Search failed:', err);
        setPeople([]);
        setLoading(false);
      }
    };

    // Debounce keystrokes; recents load immediately.
    const timer = setTimeout(run, isSearchMode ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
    // scopesKey covers contactScope/dealScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanTerm, isSearchMode, scope, uid, scopesKey]);

  return { people, loading, isSearchMode };
}
